import React, { createRef, forwardRef, useCallback, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Autocomplete } from "@material-ui/lab";
import { TextField, Checkbox } from "@material-ui/core";
import statefulField from "./StatefulField";
import { DescText } from "../App.styles";
import { useTranslation } from 'react-i18next'
import { useDecision } from '@optimizely/react-sdk'
import { useDebouncedCallback } from 'use-debounce'
import Spinner from "./Spinner";

const FieldWrapper = styled.div`
  ${({ theme }) => `
    position: relative;
    width: 100%;

    > .form-label {
      margin-bottom: .3rem;
    }

    .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"] {
      padding: 5px 6px;
    }

    .MuiInputBase-root.MuiOutlinedInput-root.MuiAutocomplete-inputRoot {
      background: ${theme.white};
      border: 1px solid ${theme.darkGreyColor}70;

      .MuiOutlinedInput-notchedOutline {
        border-color: transparent;
      }
    }
  `}
`;

const StyledAutocomplete = styled(Autocomplete)`
  [class*=PrivateNotchedOutline-root-],
  [class*=MuiOutlinedInput-notchedOutline] {
    top: 2px;
  }

  .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-outlined.Mui-focused,
  .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-outlined.MuiFormLabel-filled {
    padding: 4px;
    background: #fff;
  }
`

const InfiniteList = styled.ul`
  max-height: 300px;
  overflow: auto;
`

const InfiniteLoading = styled.li`
  padding: 0 20px;
  text-align: center;
`

const Label = styled.label`
  font-size: 0.8rem;
`;

const BaseAutoComplete = ({
  help,
  desc,
  label,
  placeholder,
  options: sourceOptions = [],
  setValue,
  onChange,
  value,
  mapValue = 'id',
  mapLabel = 'name',
  defaultText,
  multiple,
  mode = 'outline',
  creatable: sourceCreatable = false,
  parseInteger = true,
  onRemoteSearch = null,
  // This is used to load more options when the user scrolls to the bottom of the list
  onNextPage = null,
  renderLabel = null,
  filterOptions = null,
  minValue = null,
  maxValue = null,
  ...autocompleteProps
}) => {
  const { t } = useTranslation()
  const [decisionCustomPickers] = useDecision('custom-pickers')
  const [customOptions, setCustomOptions] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [inputChangeReason, setInputChangeReason] = useState(null)
  const eventType = createRef(null)
  const validOption = useCallback((option, index, self) => {
    const isUnique = self.indexOf(option) === index
    const invalid = [null, undefined]
    return isUnique && ((
      invalid.includes(option?.[mapValue]) &&
      option?.[mapLabel] === defaultText
    ) || 
      (
        !invalid.includes(option?.[mapValue]) && 
        !invalid.includes(option?.[mapLabel])
      ))
  }, [defaultText, mapLabel, mapValue])
  const creatable = useMemo(() => {
    return decisionCustomPickers?.enabled && !!sourceCreatable
  }, [decisionCustomPickers?.enabled, sourceCreatable])
  
  const getOptionSelected = useCallback((option, providedValue) => {
    const checkIsSelected = (opt, val) => {
      return String(opt?.[mapValue]) === String(val?.[mapValue]) ||
          String(opt?.[mapValue]) === String(val) ||
          String(val?.[mapValue]) === ""
    }
    const valToCheck = providedValue !== undefined ? providedValue : value;
    return Array.isArray(valToCheck) 
      ? valToCheck.some(val => checkIsSelected(option, val))
      : checkIsSelected(option, valToCheck)
  }, [mapValue, value])
  
  const getOptionDisabled = (option) => {
    return option?.disabled
  }

  const options = useMemo(() => {
    const optionsList = [
        ...sourceOptions,
        ...customOptions,
    ].filter((item, index, self) => {
        return self.findIndex(opt => String(opt?.[mapValue]) === String(item?.[mapValue])) === index
    })
    if (creatable && value && !multiple && !optionsList.find(opt => getOptionSelected(opt, value))) {
        optionsList.push({
            [mapValue]: value,
            [mapLabel]: value,
        })
    }
    // Add missing selected values to options list for infinite scroll
    if (onNextPage && multiple) {
      return [
        ...value.filter(val => 
          !optionsList.some(opt => opt?.[mapValue] === val?.[mapValue])
        ),
        ...optionsList,
      ]
    }
    return optionsList
  }, [
    sourceOptions, 
    customOptions, 
    value,
    creatable,
    getOptionSelected,
    mapLabel,
    mapValue,
    multiple,
    onNextPage,
])

  const optionsWithDefault = useMemo(() => {
    const shouldAppendDefault = !!defaultText
    return [
        // Default option:
        ...shouldAppendDefault ? [{
            [mapValue]: null,
            [mapLabel]: defaultText
        }] : [],
        ...options,
    ]
  }, [
    options,
    defaultText,
    mapLabel,
    mapValue,
  ])

  const multipleValues = useMemo(() => multiple ? (value || []).map(v => v?.[mapValue]) : [], [multiple, value, mapValue])
  const currentValue = useMemo(() => multiple
    ? options.filter(opt => multipleValues.includes(opt?.[mapValue]))
    : options.find(opt => getOptionSelected(opt, value)), [multiple, options, multipleValues, getOptionSelected, value, mapValue])
  const fallbackValue = defaultText ? optionsWithDefault[0] : null
  const valueProps = useMemo(() => ({
    options: (defaultText ? optionsWithDefault : options).filter(validOption),
    value: ![null, undefined].includes(currentValue)
      ? currentValue
      : (multiple ? [] : fallbackValue)
  }), [optionsWithDefault, options, currentValue, fallbackValue, defaultText, validOption, multiple])
  const validateNumRange = useCallback((v) => {
    let currentInputValue = String(v)
    if (minValue !== null && parseInt(currentInputValue) < parseInt(minValue)) {
        currentInputValue = String(minValue)
    }

    if (maxValue !== null && parseInt(currentInputValue) > parseInt(maxValue)) {
        currentInputValue = String(maxValue)
    }

    return currentInputValue
  }, [
    minValue, maxValue,
  ])
  const handleOnChange = useCallback((e, v) => {
    if (v === null) {
      const stateUpdate = setValue ? () => setValue(null) : null
      const callback = onChange || stateUpdate
      if (callback) callback(e, null)
      eventType.current = e?.type
      return
    }

    let selectedValue = validateNumRange(v?.[mapValue])

    if (creatable) {
        if (parseInteger) selectedValue = parseInt(selectedValue)
        if (!options.find(opt => opt[mapValue] === selectedValue)) {
            const appendOptions = [
                ...customOptions,
                {
                    [mapValue]: selectedValue,
                    [mapLabel]: selectedValue,
                }
            ]
            setCustomOptions(appendOptions)
        }
    }
    const stateUpdate = setValue ? () => setValue(selectedValue) : null
    const callback = onChange || stateUpdate
    callback && callback(e, v)
    eventType.current = e?.type
  }, [
    mapValue, mapLabel, creatable, options, customOptions, setCustomOptions, setValue, onChange, parseInteger, eventType,
    validateNumRange,
])

  const filterCreatableOptions = useCallback((_, params) => {
    const filtered = options.filter((option) => {
        const query = params.inputValue.toLowerCase().trim()
        const match = (predicate = '') => {
            return String(predicate).toLowerCase().trim().includes(query)
        }
        return match(option?.[mapValue]) || match(option?.[mapLabel])
    })
    let { inputValue: customValue } = params;
    if (customValue !== '' && filtered.length === 0) {
        customValue = validateNumRange(customValue)
        
        filtered.push({
            [mapValue]: parseInteger ? parseInt(customValue) : customValue,
            [mapLabel]: customValue,
        });
    }
    const filteredCreatableOptions = filtered.filter((value, index, self) => {
        const isUnique = self.indexOf(value) === index
        return isUnique
    })
    return filteredCreatableOptions
  }, [options, mapLabel, mapValue, parseInteger, validateNumRange])

  const getOptionElement = () => {
    try {
        return document.querySelector('.MuiAutocomplete-popper')
            .querySelector('.MuiAutocomplete-listbox')
            .querySelector('.MuiAutocomplete-option')
    } catch {
        return null
    }
  }

  const renderInput = useCallback((params) => {
    return (
        <TextField 
            {...params} 
            label={mode === 'outline' ? label : undefined}
            placeholder={placeholder}
            variant="outlined" 
            
        />
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, mode, placeholder])

  const onKeyUp = useCallback(e => {
    // Open autocomplete when user click tab then enter.
    // Autocomplete input cannot be focused programmatically,
    // so that we need to target the pop up indicator directly.
    try {
        const trigger = e.target.parentNode.querySelector('.MuiAutocomplete-popupIndicator')
        const optionElement = getOptionElement()
        if (e.key !== "Enter" || trigger === null) return;
        if (creatable && optionElement) {
            // Select custom option on enter;
            // E.g.: type 'Hats', hit enter, field blurs, value added, nothing more required.
            optionElement.click()
        } else {
            trigger.click();
        }
    } catch {}
  }, [creatable])

  const commitCreatableInput = useCallback((e, currentInputValue) => {
    let selectedValue = validateNumRange(currentInputValue)
    if (parseInteger) selectedValue = parseInt(selectedValue)

    const matchingOption = options.find(opt => String(opt[mapValue]) === String(selectedValue))
    const option = matchingOption || {
        [mapValue]: selectedValue,
        [mapLabel]: selectedValue,
    }

    if (!matchingOption) {
        setCustomOptions([
            ...customOptions,
            option,
        ])
    }

    const stateUpdate = setValue ? () => setValue(selectedValue) : null
    const callback = onChange || stateUpdate
    callback && callback(e, option)
  }, [
    customOptions, mapLabel, mapValue, onChange, options, parseInteger, setCustomOptions, setValue, validateNumRange,
  ])

  const onBlur = useCallback((e) => {
    let currentInputValue = validateNumRange(inputValue)

    if (
        !multiple &&
        currentInputValue === ''
    ) {
        if (inputChangeReason === 'clear' && autocompleteProps.disableClearable !== true) {
            const stateUpdate = setValue ? () => setValue(null) : null;
            const callback = onChange || stateUpdate;
            if (callback) callback(e, null);
        }
        return
    }

    if (creatable && inputChangeReason === 'input') {
        setInputValue(currentInputValue)
        commitCreatableInput(e, currentInputValue)
    }
  }, [
    creatable, multiple, inputValue, validateNumRange,
    inputChangeReason, autocompleteProps.disableClearable, onChange, setValue, commitCreatableInput,
])

  const onInputChange = useCallback((e, v, reason) => {
    let newInputValue = v

    if (reason === 'reset') {
        if (!e && multiple && onRemoteSearch) {
            // Ignore programmatic resets when doing remote search in multi-select.
            // This prevents the input from clearing when options arrive from the server.
            return;
        }
        newInputValue = validateNumRange(newInputValue)
    }
    
    setInputValue(newInputValue);
    setInputChangeReason(reason);

    if (onRemoteSearch && newInputValue !== defaultText && reason === 'input') {
        onRemoteSearch(newInputValue)
    }
  }, [setInputValue, onRemoteSearch, defaultText, validateNumRange, multiple])

  const listboxRef = useRef(null);
  const scrollPositionRef = useRef(0);

  const handleScroll = useDebouncedCallback(async (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target || {};
    const threshold = 10;

    if (scrollTop === 0) {
      return
    }

    if (listboxRef.current) {
      scrollPositionRef.current = scrollTop;

      const keepListboxScrollPosition = () => {
        setTimeout(() => {
          listboxRef.current.scrollTop = scrollPositionRef.current;
        }, 0);
      }

      keepListboxScrollPosition()
  
      if (scrollHeight - scrollTop <= clientHeight + threshold) {
        onNextPage && await onNextPage(event)
        keepListboxScrollPosition()
      }
    }
  }, 600)

  const InfiniteListBox = forwardRef(function ({ children, ...rest }, _) {
    return (
      <InfiniteList 
        ref={listboxRef} 
        role="listbox" 
        className="MuiAutocomplete-listbox" 
        onScroll={handleScroll}
        {...rest}
      >
        {children}
        {autocompleteProps?.loading ? <InfiniteLoading><Spinner size="0.875rem" /></InfiniteLoading> : null}
      </InfiniteList>
    )
  })
  
  return (
    <FieldWrapper className={`form-${mode}`}>
        {
          mode === 'outline'
            ? null
            : (
              <Label className="form-label">{label}</Label>
            )
        }
        <StyledAutocomplete
            {...autocompleteProps}
            {...valueProps}
            onChange={handleOnChange}
            clearOnBlur={false}
            filterOptions={
              creatable
                  ? filterCreatableOptions
                  : filterOptions || undefined
            }
            ListboxComponent={
              onNextPage
                ? InfiniteListBox
                : undefined
            }
            getOptionLabel={(option) => {
              const showLabel = str => {
                  return typeof renderLabel === 'function'
                      ? renderLabel(str)
                      : str
              }
              if (option?.[mapLabel]) {
                return showLabel(String(option?.[mapLabel]))
              }
              if (defaultText) {
                  return showLabel(String(defaultText))
              }
              // Add option created dynamically
              if (creatable && option.customValue) {
                return showLabel(String(option.customValue))
              }
              return ''
            }}
            getOptionSelected={getOptionSelected}
            getOptionDisabled={getOptionDisabled}
            renderInput={renderInput}
            multiple={multiple}
            data-testid={`form-autocomplete${multiple ? '-multiple' : ''}`}
            inputValue={inputValue}
            onInputChange={onInputChange}
            onBlur={onBlur}
            onKeyUp={onKeyUp}
            noOptionsText={creatable ? t(`No options, type to add.`) : t(`No options`)}
            blurOnSelect
        />
        {
          desc
            ? <DescText>{desc}</DescText>
            : null
        }
    </FieldWrapper>
  )
}

export const FormAutocomplete = ({ stateful = true, ...baseProps }) => {
  return stateful
    ? statefulField(BaseAutoComplete)(baseProps)
    : <BaseAutoComplete {...baseProps} />
};

export const FormAutocompleteMultiple = ({
  ...autocompleteProps
}) => {
  const { mapLabel = 'name' } = autocompleteProps

  return (
    <FormAutocomplete
      {...autocompleteProps}
      renderOption={(option, { selected }) => (
        <React.Fragment>
          <Checkbox
            checked={selected}
          />
          {option[mapLabel]}
        </React.Fragment>
      )}
      multiple
    />
  )
}

export default FormAutocomplete;
